import { MorphemeGlyphTileGridView } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.link.glyphs.list";
import { MorphemeGlyphTileLink } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.link.glyphs";
import {
  useBatchedGraphemeTileData,
  type BatchedGraphemeTileDataOptions,
  type BatchedGraphemeTileData,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.ui.glyphs";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import {
  Surface,
  SurfaceContent,
  SurfaceDescription,
  SurfaceFooter,
  SurfaceHeader,
  SurfaceTitle,
} from "@-/dev.improve.haniverse.components.ui.surface";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { useBrowseMorphemePageNavigator } from "@-/dev.improve.haniverse.routes.nav.app.browse.morpheme.navigator";
import { Pressable, View } from "@-/com.npmjs.react-native";
import { useCallback, useMemo } from "react";
import {
  getPrimitiveRowGlyphs,
  PRIMITIVE_COLLAPSED_ROWS,
  PRIMITIVE_GRID_GAP,
  type getPrimitiveGridMetrics,
  type PrimitiveVirtualRow,
} from "#primitive-virtual-rows";

const PRIMITIVE_HREF = "/(nav)/(app)/browse/morpheme/[morpheme]";

type PrimitiveListRowProps = {
  metrics: ReturnType<typeof getPrimitiveGridMetrics>;
  onExpandedChange: (glyph: string, expanded: boolean) => void;
  queryOptions: BatchedGraphemeTileDataOptions;
  row: PrimitiveVirtualRow;
};

/** One finite part of a card. Expansion state belongs to the list, never to a recycled cell. */
export function PrimitiveListRow({ row, ...props }: PrimitiveListRowProps) {
  if (row.kind === "footer") {
    return (
      <Surface className="mb-1 rounded-t-none border-t-0">
        <SurfaceFooter className="justify-center px-2 py-1.5">
          <Pressable
            accessibilityLabel={`Show fewer compounds of ${row.item.glyph}`}
            className="flex-row items-center gap-0.5 py-0.5"
            onPress={() => props.onExpandedChange(row.item.glyph, false)}
          >
            <Text className="text-xs text-muted-foreground">Show less</Text>
            <Icon color="#9CA3AF" name="LuChevronUp" size={14} />
          </Pressable>
        </SurfaceFooter>
      </Surface>
    );
  }
  // Narrowed here, so the tile row can only ever receive a header or a compounds row.
  return <PrimitiveTileRow {...props} row={row} />;
}

function PrimitiveTileRow({
  metrics,
  onExpandedChange,
  queryOptions,
  row,
}: Omit<PrimitiveListRowProps, "row"> & {
  row: PrimitiveVirtualRow & { kind: "compounds" | "header" };
}) {
  const { item } = row;
  const { availableWidth, columns } = metrics;
  const graphemes = useMemo(() => getPrimitiveRowGlyphs(row, columns), [columns, row]);
  const batchedTileData = useBatchedGraphemeTileData(graphemes, queryOptions);

  if (row.kind === "compounds") {
    return (
      <Surface className="rounded-none border-y-0" testID="primitive-compounds-row">
        <SurfaceContent
          className={`px-1 pt-1 ${row.offset + columns >= item.compounds.length ? "pb-1" : ""}`}
        >
          <MorphemeGlyphTileGridView
            availableWidth={availableWidth}
            columns={columns}
            gap={PRIMITIVE_GRID_GAP}
            graphemeList={graphemes}
            href={PRIMITIVE_HREF}
            key={graphemes.join("\u0000")}
            tileData={batchedTileData}
          />
        </SurfaceContent>
      </Surface>
    );
  }
  return (
    <PrimitiveHeaderRow
      batchedTileData={batchedTileData}
      graphemes={graphemes}
      metrics={metrics}
      onExpandedChange={onExpandedChange}
      row={row}
    />
  );
}

function PrimitiveHeaderRow({
  batchedTileData,
  graphemes,
  metrics,
  onExpandedChange,
  row,
}: Pick<PrimitiveListRowProps, "metrics" | "onExpandedChange"> & {
  batchedTileData: BatchedGraphemeTileData;
  graphemes: string[];
  row: PrimitiveVirtualRow & { kind: "header" };
}) {
  const { item } = row;
  const { availableWidth, cellSize, columns } = metrics;
  const browseMorphemePageNavigator = useBrowseMorphemePageNavigator();
  const handleViewAll = useCallback(() => {
    browseMorphemePageNavigator.push.mutate({
      morpheme: item.glyph,
      params: { tab: "compounds", view: "info" },
    });
  }, [item.glyph, browseMorphemePageNavigator]);
  const characterCount = item.compounds.length;
  const visibleGlyphs = graphemes.slice(1);
  const willOverflow = characterCount > PRIMITIVE_COLLAPSED_ROWS * columns;

  return (
    <Surface
      className={row.expanded ? "rounded-b-none border-b-0" : "mb-1"}
      testID="primitive-header-row"
    >
      <SurfaceHeader className="flex-row items-center gap-3 px-2">
        <View style={{ height: cellSize, width: cellSize }}>
          <MorphemeGlyphTileLink
            href={PRIMITIVE_HREF}
            morpheme={item.glyph}
            morphemeGlyphTileSlice={batchedTileData.getSlice(item.glyph)}
            stage="unadded"
            uncolored
          />
        </View>
        <View className="min-w-0 flex-1">
          {item.gloss !== null && (
            <SurfaceTitle className="font-normal italic" numberOfLines={1}>
              {item.gloss}
            </SurfaceTitle>
          )}
          <SurfaceDescription>
            {`#${row.rank} · ${characterCount} character${characterCount === 1 ? "" : "s"}`}
          </SurfaceDescription>
        </View>
        {characterCount > 0 && (
          <Pressable className="flex-row items-center gap-0.5 py-0.5" onPress={handleViewAll}>
            <Text className="text-xs text-muted-foreground">View all</Text>
            <Icon color="#9CA3AF" name="LuChevronRight" size={14} />
          </Pressable>
        )}
      </SurfaceHeader>
      {!row.expanded && (
        <SurfaceContent className="p-1">
          {characterCount === 0 ? (
            <Text className="text-xs" colorVariant="muted">
              No characters yet
            </Text>
          ) : (
            <MorphemeGlyphTileGridView
              availableWidth={availableWidth}
              columns={columns}
              gap={PRIMITIVE_GRID_GAP}
              graphemeList={visibleGlyphs}
              href={PRIMITIVE_HREF}
              key={visibleGlyphs.join("\u0000")}
              tileData={batchedTileData}
              trailingCell={
                willOverflow ? (
                  <OverflowExpandButton
                    count={characterCount - visibleGlyphs.length}
                    glyph={item.glyph}
                    onPress={() => onExpandedChange(item.glyph, true)}
                  />
                ) : undefined
              }
            />
          )}
        </SurfaceContent>
      )}
    </Surface>
  );
}

function OverflowExpandButton({
  count,
  glyph,
  onPress,
}: {
  count: number;
  glyph: string;
  onPress: () => void;
}) {
  return (
    <Pressable
      accessibilityLabel={`Show ${count} more compounds of ${glyph}`}
      className="size-full items-center justify-center rounded-xs border border-border bg-muted/50 dark:bg-muted/70"
      onPress={onPress}
    >
      <Text className="text-sm font-semibold" colorVariant="muted">
        +{count}
      </Text>
    </Pressable>
  );
}
